import React, { useState, useEffect, useCallback, useRef } from "react";
import { RiWhatsappFill } from "react-icons/ri";
import {
  MessageCircle, FileText, RefreshCw, Plus, XCircle, Loader2,
} from "lucide-react";
import {
  PageHeader, AppButton, AppCard, AppCardHeader, AppCardTitle, AppCardContent,
  StatusBadge, EmptyState, AppBadge,
  Tabs, TabsList, TabsTrigger, TabsContent, ConfirmModal,
} from "../../ui";
import apiClient from "../../../utils/apiClient";
import WhatsAppChatInbox from "../../WhatsApp/WhatsAppChatInbox";
import { useSocket } from "../../../contexts/SocketContext";
import CreateTemplateForm from "../../WhatsApp/CreateTemplateForm";
import CommonToaster from "../../Common/CommonToaster";
import "./PlatformWhatsAppInbox.scss";

const PlatformWhatsAppInbox = () => {
  const { socket } = useSocket();
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const toastRef = useRef(null);
  toastRef.current = (msg, type = "success") => setToast({ show: true, message: msg, type });
  const showToast = useCallback((msg, type) => toastRef.current(msg, type), []);

  /* ── Inbox callbacks (normalise platform API to shared component shape) ── */
  const fetchConversations = useCallback(async (statusFilter) => {
    const q = statusFilter ? `?status=${statusFilter}` : "";
    const { data } = await apiClient.get(`/platform/whatsapp/conversations${q}`);
    return (data.conversations || []).map(c => ({
      _id: c.conversationId,
      name: c.customerName || "",
      phone: c.phone,
      preview: c.lastMessage,
      updatedAt: c.lastMessageAt,
      unreadCount: c.unreadCount,
      status: c.status,
    }));
  }, []);

  const fetchMessages = useCallback(async (convId) => {
    const { data } = await apiClient.get(`/platform/whatsapp/conversations/${convId}/messages`);
    return data.messages || []; // already oldest-first
  }, []);

  const sendReply = useCallback(async (conversation, payload) => {
    const body = typeof payload === "string"
      ? { phone: conversation.phone, message: payload }
      : { phone: conversation.phone, message: payload.text, ...payload };
    // Backend expects `message` field for text. Map text → message for compatibility.
    if (body.text && !body.message) body.message = body.text;
    delete body.text;
    await apiClient.post("/platform/whatsapp/send", body);
  }, []);

  const updateStatus = useCallback(async (convId, status) => {
    await apiClient.patch(`/platform/whatsapp/conversations/${convId}/status`, { status });
  }, []);

  return (
    <div className="platform-wa-inbox">
      <PageHeader
        title="WhatsApp Inbox"
        icon={RiWhatsappFill}
        description="Manage messages and templates"
      />

      <Tabs defaultValue="inbox" className="wa-tabs">
        <TabsList>
          <TabsTrigger value="inbox"><MessageCircle size={14} /> Inbox</TabsTrigger>
          <TabsTrigger value="templates"><FileText size={14} /> Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="inbox">
          <WhatsAppChatInbox
            fetchConversations={fetchConversations}
            fetchMessages={fetchMessages}
            sendReply={sendReply}
            updateStatus={updateStatus}
            showToast={showToast}
            socket={socket}
            mediaEndpoint="/platform/whatsapp/media"
            uploadEndpoint="/platform/whatsapp/upload-media"
          />
        </TabsContent>

        <TabsContent value="templates">
          <PlatformTemplatesTab />
        </TabsContent>
      </Tabs>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

/* ─── Platform Templates Tab ───────────────────────────────────────────────── */
const tplStatusToVariant = (s) => {
  const map = { APPROVED: "active", PENDING: "pending", REJECTED: "destructive", PAUSED: "inactive", DISABLED: "inactive" };
  return map[s] || "outline";
};

const PlatformTemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const showToast = useCallback((msg, type = "success") => setToast({ show: true, message: msg, type }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/platform/whatsapp/templates");
      setTemplates(data.templates || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncTemplates = async () => {
    setSyncing(true);
    try {
      const { data } = await apiClient.post("/platform/whatsapp/templates/sync");
      showToast(data.message || "Templates synced");
      load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Sync failed", "error");
    }
    setSyncing(false);
  };

  const handleCreate = async (formData) => {
    const { data } = await apiClient.post("/platform/whatsapp/templates/create", formData);
    showToast(data.message || "Template submitted");
    setShowCreate(false);
    load();
  };

  const [deleteTemplateName, setDeleteTemplateName] = useState(null);

  const handleDelete = (name) => setDeleteTemplateName(name);

  const confirmDelete = async () => {
    const name = deleteTemplateName;
    setDeleteTemplateName(null);
    setDeleting(name);
    try {
      await apiClient.delete(`/platform/whatsapp/templates/${encodeURIComponent(name)}`);
      showToast("Template deleted");
      load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Delete failed", "error");
    }
    setDeleting(null);
  };

  return (
    <>
      {showCreate ? (
        <AppCard>
          <AppCardHeader>
            <AppCardTitle><FileText size={18} /> Create Template</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <CreateTemplateForm onSubmit={handleCreate} onCancel={() => setShowCreate(false)} />
          </AppCardContent>
        </AppCard>
      ) : (
        <AppCard>
          <AppCardHeader>
            <div className="wa-card-header-row">
              <AppCardTitle><FileText size={18} /> Platform Templates</AppCardTitle>
              <div className="wa-inline-actions">
                <span className="wa-text-muted wa-text-sm">
                  {templates.length} template(s) · Approved templates are recommended to institutes
                </span>
                <AppButton size="sm" onClick={() => setShowCreate(true)}>
                  <Plus size={13} /> Create Template
                </AppButton>
                <AppButton size="sm" onClick={syncTemplates} disabled={syncing}>
                  <RefreshCw size={13} className={syncing ? "wa-spin" : ""} />
                  {syncing ? "Syncing..." : "Sync from Meta"}
                </AppButton>
              </div>
            </div>
          </AppCardHeader>
          <AppCardContent>
            {loading ? (
              <div className="wa-loader"><Loader2 size={20} className="wa-spin" /> Loading…</div>
            ) : templates.length === 0 ? (
              <EmptyState
                icon={<FileText size={32} />}
                title="No Templates"
                description='Click "Sync from Meta" to fetch your approved templates, or create a new one.'
              />
            ) : (
              <div className="wa-table-wrap">
                <table className="wa-table">
                  <thead>
                    <tr><th>Name</th><th>Language</th><th>Category</th><th>Status</th><th>Params</th><th></th></tr>
                  </thead>
                  <tbody>
                    {templates.map(t => (
                      <tr key={t._id}>
                        <td className="wa-table__mono">{t.name}</td>
                        <td>{t.language}</td>
                        <td><AppBadge variant="secondary" size="sm">{t.category || "—"}</AppBadge></td>
                        <td><StatusBadge status={tplStatusToVariant(t.status)}>{t.status}</StatusBadge></td>
                        <td>{t.parameterCount}</td>
                        <td>
                          <AppButton
                            variant="ghost" size="sm"
                            onClick={() => handleDelete(t.name)}
                            disabled={deleting === t.name}
                            title="Delete template"
                          >
                            {deleting === t.name ? <Loader2 size={13} className="wa-spin" /> : <XCircle size={13} />}
                          </AppButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </AppCardContent>
        </AppCard>
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(t => ({ ...t, show: false }))}
      />

      <ConfirmModal
        show={!!deleteTemplateName}
        onHide={() => setDeleteTemplateName(null)}
        onConfirm={confirmDelete}
        title="Delete Template"
        message={`Delete template "${deleteTemplateName}"? This will also delete it from Meta.`}
        confirmText="Delete"
        variant="danger"
      />
    </>
  );
};

export default PlatformWhatsAppInbox;
